import React from 'react';
import styled from '@emotion/native';
import { Linking, TouchableOpacity, View } from 'react-native';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import CarouselImageOne from '@/assets/images/CarouselImageOne.png';
import CarouselImageTwo from '@/assets/images/CarouselImageTwo.png';
import CarouselImageThree from '@/assets/images/CarouselImageThree.png';
import CarouselImageFour from '@/assets/images/CarouselImageFour.png';
import CarouselImageFive from '@/assets/images/CarouselImageFive.png';
import CarouselImageSix from '@/assets/images/CarouselImageSix.png';
import CarouselImageSeven from '@/assets/images/CarouselImageSeven.png';
import CarouselImageEight from '@/assets/images/CarouselImageEight.png';
import CarouselImageNine from '@/assets/images/CarouselImageNine.png';
import CarouselImageTen from '@/assets/images/CarouselImageTen.png';
import {
  WELCOME_SECTION_BREAKPOINT,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
} from '@/shared/styles/constants';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';

const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/emmestyling/';

const INSTAGRAM_LINK_LABEL = 'Follow EMME Styling on Instagram @emmestyling';

const GRID_IMAGES = [
  CarouselImageOne,
  CarouselImageTwo,
  CarouselImageThree,
  CarouselImageFour,
  CarouselImageFive,
  CarouselImageSix,
  CarouselImageSeven,
  CarouselImageEight,
  CarouselImageNine,
  CarouselImageTen,
];

const GAP = 12;

function getColumnCount(innerWidth: number): number {
  if (innerWidth >= WELCOME_SECTION_BREAKPOINT) {
    return 5;
  }
  if (innerWidth >= 520) {
    return 3;
  }
  return 2;
}

const KeepInTouchInstagramStaticGrid = () => {
  const { width, isWide: isWideGrid } = useWelcomeSectionLayout();
  const horizontalPadding = isWideGrid
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA;
  const innerWidth =
    Math.min(width, WELCOME_SECTION_MAX_WIDTH) - horizontalPadding * 2;
  const columnCount = getColumnCount(innerWidth);
  const itemWidth = (innerWidth - GAP * (columnCount - 1)) / columnCount;
  const itemHeight = (itemWidth * 5) / 4;

  return (
    <Grid>
      {GRID_IMAGES.map((source, index) => {
        const sizeStyle = { width: itemWidth, height: itemHeight };
        if (index === 0) {
          return (
            <InstagramGridLeadLink
              key={index}
              sizeStyle={sizeStyle}
              source={source}
            />
          );
        }
        return (
          <DecorativeCell
            key={index}
            accessible={false}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={sizeStyle}
          >
            <CellImage
              source={source}
              accessible={false}
              importantForAccessibility="no-hide-descendants"
            />
          </DecorativeCell>
        );
      })}
    </Grid>
  );
};

interface InstagramGridLeadLinkProps {
  sizeStyle: { width: number; height: number };
  source: (typeof GRID_IMAGES)[number];
}

const InstagramGridLeadLink = ({
  sizeStyle,
  source,
}: InstagramGridLeadLinkProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();

  const handleOpenInstagram = async () => {
    try {
      await Linking.openURL(INSTAGRAM_PROFILE_URL);
    } catch (error) {
      console.error('Error opening Instagram profile URL:', error);
    }
  };

  return (
    <LinkCell
      onPress={handleOpenInstagram}
      accessibilityRole="link"
      accessibilityLabel={INSTAGRAM_LINK_LABEL}
      style={[sizeStyle, IS_WEB && isHovered ? { opacity: 0.9 } : undefined]}
      {...webHoverProps}
    >
      <CellImage source={source} />
    </LinkCell>
  );
};

const Grid = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: GAP,
  justifyContent: 'flex-start',
});

const LinkCell = styled(TouchableOpacity)({
  borderRadius: 4,
  overflow: 'hidden',
});

const DecorativeCell = styled(View)({
  borderRadius: 4,
  overflow: 'hidden',
});

const CellImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'cover',
});

export default KeepInTouchInstagramStaticGrid;
